import { memo } from 'react';
import { Box, Button, Modal, SpaceBetween, Spinner } from '@amzn/awsui-components-core';

import { FeedbackRating } from '../feedback-service/FeedbackTypes';
import { MetaDivider } from './MetaDivider';
import { ContentFeedbackViewModel, useContentFeedbackViewModel } from './ContentFeedbackViewModel';

interface ContentFeedbackContentProps {
  /** Presentation state for the control. */
  readonly viewModel: ContentFeedbackViewModel;
}

/** Renders the like/dislike control from a supplied view model; the bound entry point is {@link ContentFeedback}. */
const ContentFeedbackContent = memo(function ContentFeedbackContent({
  viewModel: vm,
}: ContentFeedbackContentProps) {
  if (!vm.isVisible) return null;
  if (vm.isLoading) return <Spinner size="normal" />;

  const isLikePrompt = vm.promptRating === FeedbackRating.Like;

  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <MetaDivider />
      <Button
        variant="icon"
        iconName={vm.userRating === FeedbackRating.Like ? 'thumbs-up-filled' : 'thumbs-up'}
        ariaLabel="Like"
        disabled={vm.isVoting}
        onClick={() => vm.vote(FeedbackRating.Like)}
      />
      <Box variant="span">{vm.likeCount}</Box>
      <Button
        variant="icon"
        iconName={vm.userRating === FeedbackRating.Dislike ? 'thumbs-down-filled' : 'thumbs-down'}
        ariaLabel="Dislike"
        disabled={vm.isVoting}
        onClick={() => vm.vote(FeedbackRating.Dislike)}
      />
      <Box variant="span">{vm.dislikeCount}</Box>
      <Modal
        visible={vm.isFeedbackPromptVisible}
        onDismiss={vm.dismissFeedbackPrompt}
        header={isLikePrompt ? 'Like' : 'Dislike'}
        size="medium"
        closeAriaLabel="Close modal"
      >
        <Box variant="p">Thanks for the feedback!</Box>
        <Box variant="p" padding={{ bottom: 's' }}>
          {isLikePrompt
            ? 'If you can, please give us more details on what about this was good.'
            : 'If you can, please give us more details on what about this needs work.'}
        </Box>
        <Button onClick={vm.submitWrittenFeedback}>Submit feedback</Button>
      </Modal>
    </SpaceBetween>
  );
});

/** Like/dislike control for published content, shown in the title header area. */
export const ContentFeedback = memo(function ContentFeedback() {
  const vm = useContentFeedbackViewModel();
  return <ContentFeedbackContent viewModel={vm} />;
});

export { ContentFeedbackContent };
